import { useState } from "react";
import { Check, Send } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, formatApiError } from "../lib/api";
import { CONTACT_REASONS } from "../data/content";
import Seo from "../components/Seo";

export default function ContactPage() {
  const [form, setForm] = useState({ reason: "", full_name: "", email: "", organization: "", details: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reason) { setStatus({ state: "error", msg: "Please select a reason for contact." }); return; }
    setStatus({ state: "loading", msg: "" });
    try {
      const { data } = await api.post("/contact", form);
      setStatus({ state: "done", msg: data.message });
      setForm({ reason: "", full_name: "", email: "", organization: "", details: "" });
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err.response?.data?.detail) });
    }
  };

  const field = "w-full bg-surface border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors";

  return (
    <div data-testid="contact-page" className="grid-bg grain min-h-[80vh]">
      <Seo title="Contact" path="/contact" description="Get in touch with My Eco Engineer, coaching, household systems setup, media, partnerships, and product inquiries." />
      <div className="mx-auto max-w-3xl px-5 sm:px-8 py-20">
        <p className="mono-label">Contact</p>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight">Get in touch.</h1>
        <p className="mt-5 max-w-xl text-base text-foreground/65 leading-relaxed">
          For media inquiries, advisory engagements, workshops, partnerships, or product-related questions, submit the form below.
        </p>

        {status.state === "done" ? (
          <div data-testid="contact-success" className="mt-10 border border-primary/40 bg-primary/5 p-8 flex items-start gap-4">
            <Check className="h-6 w-6 text-primary shrink-0" />
            <div>
              <h3 className="font-display text-xl">Inquiry received</h3>
              <p className="mt-2 text-sm text-foreground/70">{status.msg}</p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="mt-10 space-y-5">
            <div>
              <label className="mono-label mb-2 block">Reason for contact</label>
              <Select value={form.reason} onValueChange={(v) => setForm((f) => ({ ...f, reason: v }))}>
                <SelectTrigger data-testid="contact-reason" className="bg-surface border-border rounded-none py-6 text-sm">
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent className="bg-surface-elevated border-border">
                  {CONTACT_REASONS.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`reason-${r.replace(/[^a-z]/gi, "-").toLowerCase()}`}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="mono-label mb-2 block">Full name</label>
                <input data-testid="contact-name" required value={form.full_name} onChange={set("full_name")} className={field} placeholder="Your full name" />
              </div>
              <div>
                <label className="mono-label mb-2 block">Email address</label>
                <input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} className={field} placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <label className="mono-label mb-2 block">Organization (optional)</label>
              <input data-testid="contact-org" value={form.organization} onChange={set("organization")} className={field} placeholder="Company / organization" />
            </div>
            <div>
              <label className="mono-label mb-2 block">Inquiry details</label>
              <textarea data-testid="contact-details" required rows={6} value={form.details} onChange={set("details")} className={field} placeholder="Tell us about your inquiry…" />
            </div>
            {status.state === "error" && <p data-testid="contact-error" className="text-xs text-destructive">{status.msg}</p>}
            <button data-testid="contact-submit" disabled={status.state === "loading"}
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium text-sm px-7 py-3.5 hover:bg-primary-hover transition-colors disabled:opacity-60 shadow-[0_0_20px_rgba(0,229,153,0.3)]">
              {status.state === "loading" ? "Sending…" : "Send inquiry"}
              <Send className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </form>
        )}

        <div data-testid="contact-legal-entity" className="mt-12 border-t border-border pt-6 text-sm text-foreground/55 leading-relaxed">
          <p className="font-medium text-foreground/70">My Eco Engineer</p>
          <p>Operated by Colimon Systems Group, LLC</p>
          <p>
            General inquiries:{" "}
            <a href="mailto:info@myecoengineer.com" className="text-primary hover:underline">info@myecoengineer.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
